/**
 * Permisos granulares de archivos (spec 2026-10-01-permisos-granulares §3).
 * Una sola función para normalizar cualquier fuente de permisos (rol global,
 * globalPermissions de carpeta, matriz del cliente, matriz de carpeta). La
 * usan el motor, las pantallas y el script de arreglo (copia en
 * daysi-cloud-functions/scripts/granular_migration_lib.js, con test de que dan lo mismo).
 *
 *  - Fuente NUEVA (documents.granular === true): cada clave vale sola.
 *  - Fuente VIEJA: las claves nuevas heredan de `create` / `delete`, y
 *    `deleteLinked` exige `delete` en la misma fuente (la unión entre fuentes
 *    la resuelve permission_merge con legacyLinkedRaw).
 * Se lee con truthiness (!!) como el orFlag de siempre. Módulo puro.
 */

export const GRANULAR_DOC_FLAGS = [
  'read', 'create', 'createXml', 'createLink', 'createReference',
  'update', 'disapprove', 'delete', 'deleteXml', 'deleteReference',
  'deleteLinked', 'accounting', 'deleteAccounting', 'download',
];

export const INHERIT = {
  createXml: 'create', createLink: 'create', createReference: 'create',
  disapprove: 'delete', deleteXml: 'delete', deleteReference: 'delete',
};

const docsOf = (perms) => (perms && typeof perms === 'object' && perms.documents && typeof perms.documents === 'object'
  ? perms.documents : null);

export const isGranular = (perms) => docsOf(perms)?.granular === true;

/** deleteLinked crudo de una fuente vieja (para la unión en permission_merge). */
export const legacyLinkedRaw = (perms) => {
  const d = docsOf(perms);
  return !!d && d.granular !== true && !!d.deleteLinked;
};

export const toGranular = (perms) => {
  const d = docsOf(perms);
  if (!d) return perms;
  const documents = {};
  GRANULAR_DOC_FLAGS.forEach((f) => { documents[f] = !!d[f]; });
  if (d.granular !== true) {
    Object.entries(INHERIT).forEach(([flag, src]) => {
      documents[flag] = d[flag] !== undefined ? !!d[flag] : !!d[src];
    });
    documents.deleteLinked = !!d.deleteLinked && !!d.delete;
  }
  documents.granular = true;
  return { ...perms, documents };
};

/**
 * Un permiso "crudo" (p. ej. el rol global que Búsqueda le pasa al cajón) con
 * las claves granulares agregadas por herencia, conservando todo lo demás
 * (level, root, supervisor, users, folders…).
 */
export const withGranularDocuments = (perms) => {
  const d = docsOf(perms);
  if (!d) return perms;
  return { ...perms, documents: { ...d, ...toGranular(perms).documents } };
};

export default toGranular;
